<template>
  <div class="flex flex-col gap-2 overflow-hidden h-full">
    <VanCellGroup ref="scrollContainer" inset class="mt-4 flex-1 overflow-auto">
      <div
        :class="[
          'flex-1',
          isEditMode
            ? 'border border-primary rounded-xl overflow-hidden'
            : 'pb-4',
        ]"
      >
        <VanEmpty
          v-if="fields.length === 0"
          :description="$t('Message.noAttribute')"
        />
        <CustomRichTextCell
          v-else
          v-for="(field, i) in fields"
          :key="field.id"
          :title="field.displayName"
          :content="field.value"
          :cb="(content: string) => onBlur(content, field)"
          :is-dragging-mode="isEditMode"
          :module-name="moduleStore.module?.moduleIdMap[moduleId]?.name"
          :class="[
            dragTargetIndex === i && 'border-2 border-primary',
            dragIndex === i && 'bg-primary/50 ',
          ]"
          :data-index="i"
          :draggable="isEditMode"
          @dragstart="dragStart($event, i)"
          @dragenter="dragEnter($event, i)"
          @dragend="dragEnd"
          @dragover="dragOver"
          @touchstart="touchStart($event, i, fields.length)"
          @touchmove="touchMove"
          @touchend="touchEnd"
        >
          <template #label>
            <div class="flex justify-between items-center w-full">
              <p>{{ field.displayName }}</p>

              <div
                v-if="isEditMode && !field.isPreset"
                class="flex items-center gap-1"
              >
                <button
                  class="btn btn-ghost btn-outline btn-xs"
                  @click="updateQuestionName(field)"
                >
                  <i class="iconify mingcute--edit-2-line" />
                </button>
                <button
                  class="btn btn-ghost btn-outline btn-xs"
                  @click.stop="
                    moduleStore.deleteQuestion(Number(moduleId), field.id)
                  "
                >
                  <i class="iconify mingcute--delete-2-line" />
                </button>
              </div>
            </div>
          </template>
        </CustomRichTextCell>
      </div>
    </VanCellGroup>

    <CustomModuleEdit
      class="px-4"
      :module-id="moduleId"
      v-model:is-dragging-mode="isEditMode"
      enable-dragging
      enable-template
    />
  </div>
</template>

<script setup lang="ts">
import { useGlobalModal } from "@/stores/globalModal";
import { ModuleFieldItem, useModule } from "@/stores/module";
import { useDrag } from "@/use/drag";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";

import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
const moduleStore = useModule();

const props = defineProps<{
  moduleId: number;
  isSaving: boolean;
}>();

const { t } = useI18n();

const emit = defineEmits<{
  "update:is-saving": [boolean];
}>();

const fields = computed(() => {
  const map: { [id: number]: ModuleFieldItem } = {};
  const moduleOrder = moduleStore.module?.getMateModuleOrderById(
    props.moduleId
  );
  const originList = moduleStore.module!.moduleFieldsMap[props.moduleId]!;
  if (moduleOrder?.children?.length) {
    originList.forEach((v) => {
      map[v.id] = v;
    });
    return moduleOrder.children.map((v) => map[v]).filter((v) => v) || [];
  }
  return originList || [];
});

const onBlur = async (content: string, field: ModuleFieldItem) => {
  field.value = content;
  emit("update:is-saving", true);
  await moduleStore.updateMateModule(props.moduleId!);
  emit("update:is-saving", false);
};

const scrollContainer = ref<HTMLElement>();
const isEditMode = ref<boolean>(false);

const updateQuestionName = async (module: ModuleFieldItem) => {
  if (isEditMode.value)
    useGlobalModal().openTextInputModal({
      title: t("User.mate.attribute.updateCustom"),
      callback: async (name?: string) => {
        if (!name) return;
        const toast = showLoadingToast({
          message: t("Message.updating"),
          duration: 0,
        });
        try {
          await moduleStore.updateQuestion(module.moduleId, module.id, name);
          toast.close();
          showSuccessToast(t("Message.saveSuccess"));
        } catch (e) {
          showFailToast(t("Message.saveFailed"));
          toast.close();
          throw e;
        }
      },
    });
};
const {
  dragIndex,
  dragTargetIndex,
  dragStart,
  dragEnd,
  dragEnter,
  dragOver,
  touchStart,
  touchMove,
  touchEnd,
} = useDrag(
  { isDraggingMode: isEditMode, scrollContainer },
  (originIndex, targetIndex) => {
    if (originIndex === targetIndex) return;
    const [removed] = fields.value.splice(originIndex, 1);
    fields.value.splice(targetIndex, 0, removed);

    const list = fields.value.map((v) => v.id);

    const moduleOrder = moduleStore.module!.getMateModuleOrderById(
      props.moduleId
    );
    const module = moduleStore.module!.moduleIdMateModuleMap[props.moduleId];
    if (!moduleOrder) {
      moduleStore.module!.addMateModule(module, list);
    } else {
      moduleOrder.children = list;
      moduleStore.module?.updateEntityOrder();
    }
  }
);
</script>
